import { useRef, type RefObject } from "react";
import { X } from "lucide-react";
import {
  addDays,
  displayToIsoDate,
  maskDisplayDate,
  toDisplayDate,
  toIsoDate,
} from "@/features/quotation/dateInput";

type QuickDateChooserProps = {
  customDateText: string;
  confirmLabel: string;
  isPending: boolean;
  onCustomDateChange: (value: string) => void;
  onPickToday: () => void;
  onPickTomorrow: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
  showCancelIcon?: boolean;
  today?: Date;
  confirmRef?: RefObject<HTMLButtonElement | null>;
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.625rem 0.75rem",
  background: "var(--login-surface)",
  border: "1px solid var(--login-border)",
  borderRadius: "10px",
  color: "var(--login-text)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.9375rem",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 700,
  fontSize: "0.75rem",
  color: "var(--login-muted)",
};

const btnPrimary: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  minHeight: "44px",
  background: "var(--copy-primary, #a0eb70)",
  border: "none",
  borderRadius: "10px",
  color: "var(--copy-primary-ink, #183f3b)",
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 700,
  fontSize: "0.875rem",
  cursor: "pointer",
};

const quickDateOptionStyle: React.CSSProperties = {
  flex: 1,
  minWidth: "120px",
  padding: "0.9rem 1rem",
  borderRadius: "10px",
  border: "1px solid var(--login-border)",
  background: "var(--login-surface)",
  color: "var(--login-text)",
  cursor: "pointer",
  textAlign: "left",
  transition: "border-color 0.15s, background-color 0.15s",
};

const quickDateHintStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.8125rem",
  color: "var(--login-muted)",
  margin: 0,
};

export function QuickDateChooser({
  customDateText,
  confirmLabel,
  isPending,
  onCustomDateChange,
  onPickToday,
  onPickTomorrow,
  onConfirm,
  onCancel,
  showCancelIcon = false,
  today = new Date(),
  confirmRef,
}: QuickDateChooserProps) {
  const tomorrow = addDays(today, 1);
  const todayIso = toIsoDate(today);
  const tomorrowIso = toIsoDate(tomorrow);
  const customDateIso = displayToIsoDate(customDateText);
  const isTodaySelected = customDateIso === todayIso;
  const isTomorrowSelected = customDateIso === tomorrowIso;
  const isCustomSelected =
    customDateText.length > 0 && !isTodaySelected && !isTomorrowSelected;
  const customInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      className="quick-date-chooser"
      style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
        <button
          type="button"
          className="quick-date-chooser__option"
          aria-pressed={isTodaySelected}
          onClick={onPickToday}
          disabled={isPending}
          style={{
            ...quickDateOptionStyle,
            borderColor: isTodaySelected
              ? "var(--selection-accent)"
              : "var(--login-border)",
            background: isTodaySelected
              ? "var(--selection-surface)"
              : "var(--login-surface)",
            boxShadow: isTodaySelected
              ? "inset 0 -2px var(--selection-accent)"
              : "none",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: "var(--login-text)",
            }}
          >
            Hoje
          </p>
          <p style={{ ...quickDateHintStyle, marginTop: "0.2rem" }}>
            {toDisplayDate(today)}
          </p>
        </button>

        <button
          type="button"
          className="quick-date-chooser__option"
          aria-pressed={isTomorrowSelected}
          onClick={onPickTomorrow}
          disabled={isPending}
          style={{
            ...quickDateOptionStyle,
            borderColor: isTomorrowSelected
              ? "var(--selection-accent)"
              : "var(--login-border)",
            background: isTomorrowSelected
              ? "var(--selection-surface)"
              : "var(--login-surface)",
            boxShadow: isTomorrowSelected
              ? "inset 0 -2px var(--selection-accent)"
              : "none",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: "var(--login-text)",
            }}
          >
            Amanhã
          </p>
          <p style={{ ...quickDateHintStyle, marginTop: "0.2rem" }}>
            {toDisplayDate(tomorrow)}
          </p>
        </button>
      </div>

      <div
        onClick={() => customInputRef.current?.focus()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.375rem",
          padding: "0.9rem 1rem",
          borderRadius: "10px",
          border: `1px solid ${
            isCustomSelected ? "var(--selection-accent)" : "var(--login-border)"
          }`,
          background: isCustomSelected
            ? "var(--selection-surface)"
            : "var(--login-surface)",
          boxShadow: isCustomSelected
            ? "inset 0 -2px var(--selection-accent)"
            : "none",
          cursor: "text",
        }}
      >
        <label style={labelStyle}>Outra data</label>
        <input
          ref={customInputRef}
          className="quick-date-chooser__input"
          inputMode="numeric"
          placeholder="DD/MM/YYYY"
          value={customDateText}
          onChange={(e) => onCustomDateChange(maskDisplayDate(e.target.value))}
          style={{
            ...inputStyle,
            borderColor: isCustomSelected
              ? "var(--selection-accent)"
              : "var(--login-border)",
          }}
        />
      </div>

      <div style={{ display: "flex", gap: "0.75rem" }}>
        <button
          ref={confirmRef}
          type="button"
          className="btn-primary quick-date-chooser__primary"
          style={btnPrimary}
          onClick={onConfirm}
          disabled={!customDateIso || isPending}
        >
          {confirmLabel}
        </button>
        {onCancel ? (
          <button
            type="button"
            className="btn-sm quick-date-chooser__cancel"
            onClick={onCancel}
            disabled={isPending}
            style={{
              minWidth: "96px",
              minHeight: "44px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.35rem",
              border: "1px solid var(--login-border)",
              borderRadius: "10px",
              background: "var(--login-surface)",
              color: "var(--login-text)",
              font: "700 0.8125rem 'DM Sans', sans-serif",
              letterSpacing: 0,
              textTransform: "none",
            }}
          >
            {showCancelIcon ? <X size={16} aria-hidden="true" /> : null}
            Cancelar
          </button>
        ) : null}
      </div>
    </div>
  );
}
